import { graphql } from 'gatsby'
import React, { useMemo } from 'react'
import ColorableSimpleBlock from '../components/colorable-simple-block'
import Hero from '../components/hero'
import ImageBlock from '../components/image-block'
import Layout from '../components/layout'
import {
  GroupCompaniesOverlay,
  HeroOverlay,
  OurScaleOverlay,
} from '../components/overlays/about'
import ProfileList from '../components/profile-list'
import SimpleBlock from '../components/simple-block'
import { flatten } from '../utils/data'

const AboutPage = ({ data: { about } }) => {
  const page = useMemo(
    () =>
      flatten(about, [
        'hero',
        'story',
        'scale',
        'group',
        'team',
        'sisterCompanies',
      ]),
    [about]
  )

  return (
    <Layout
      seoMetaTags={page.seoMetaTags}
      title={page.seoSettings && page.seoSettings.title}
    >
      <>
        <section className="relative" id="hero">
          <HeroOverlay />
          <Hero
            darkMode={true}
            title={page.hero.titleNode}
            description={(page.hero.description || {}).content}
            image={page.hero.image}
            mobileImage={page.hero.mobileImage}
            size="small"
          />
        </section>
        <section className="relative pt-16 pb-8 md:pt-24" id="introduction">
          <SimpleBlock description={page.introductionNode} />
        </section>
        <section className="relative pt-8" id="our-story">
          <ColorableSimpleBlock
            title={page.story.title}
            description={page.story.descriptionNode}
          />
        </section>
        <section className="relative py-16" id="our-scale">
          <OurScaleOverlay />
          <ImageBlock
            vertical={true}
            title={page.scale.title}
            description={page.scale.descriptionNode}
            image={page.scale.image}
          />
        </section>
        {page.group && (
          <section
            className="relative py-16 bg-primary-variant"
            id="group-companies"
          >
            <GroupCompaniesOverlay />
            <SimpleBlock
              titleColorClass="text-primary-variant"
              centered={false}
              descriptionClass="md:text-left"
              descriptionColorClass="text-primary"
              title={page.group.title}
              description={page.group.descriptionNode}
            />
          </section>
        )}
        <section className="relative py-16 md:pt-24" id="our-team">
          <ProfileList
            title={page.team.title}
            description={page.team.descriptionNode}
            profiles={page.team.profiles}
          />
        </section>
        <section
          className="relative py-16 bg-primary-variant"
          id="group-companies"
        >
          <SimpleBlock
            titleColorClass="text-primary-variant"
            centered={false}
            descriptionClass="md:text-left"
            descriptionColorClass="text-primary"
            title={page.sisterCompanies.title}
            description={page.sisterCompanies.descriptionNode}
          />
        </section>
      </>
    </Layout>
  )
}

export default AboutPage

export const query = graphql`
  query AboutQuery {
    about: datoCmsAboutPage {
      seoSettings {
        title
      }
      seoMetaTags {
        ...GatsbyDatoCmsSeoMetaTags
      }
      hero {
        mobileImage {
          fluid(
            maxWidth: 767
            imgixParams: { fm: "jpg", auto: "compress", q: 75 }
          ) {
            ...GatsbyDatoCmsFluid
          }
        }
        image {
          fluid(
            maxWidth: 1920
            imgixParams: { fm: "jpg", auto: "compress", q: 75 }
          ) {
            ...GatsbyDatoCmsFluid
          }
        }
        titleNode {
          childMarkdownRemark {
            html
          }
        }
        description {
          content {
            ... on DatoCmsSlideText {
              model {
                apiKey
              }
              textNode {
                childMarkdownRemark {
                  html
                }
              }
            }
            ... on DatoCmsSlideImage {
              model {
                apiKey
              }
              contentNode {
                childMarkdownRemark {
                  html
                }
              }
            }
          }
        }
      }
      introductionNode {
        childMarkdownRemark {
          html
        }
      }
      story {
        title
        descriptionNode {
          childMarkdownRemark {
            html
          }
        }
      }
      scale {
        title
        descriptionNode {
          childMarkdownRemark {
            html
          }
        }
        image {
          fluid(
            maxWidth: 1440
            imgixParams: { fm: "jpg", auto: "compress", q: 75 }
          ) {
            ...GatsbyDatoCmsFluid
          }
        }
      }
      group {
        title
        descriptionNode {
          childMarkdownRemark {
            html
          }
        }
      }
      team {
        title
        descriptionNode {
          childMarkdownRemark {
            html
          }
        }
        profiles {
          image {
            fluid(maxWidth: 767, imgixParams: { fm: "png", auto: "format" }) {
              ...GatsbyDatoCmsFluid
            }
          }
          name
          jobTitle
          descriptionNode {
            childMarkdownRemark {
              html
            }
          }
        }
      }
      sisterCompanies {
        title
        descriptionNode {
          childMarkdownRemark {
            html
          }
        }
      }
    }
  }
`
